import { useCallback, useEffect, useState } from 'react';

/*
 * Chrome "Install app" (PWA) plumbing.
 *
 * Chrome fires `beforeinstallprompt` once the site meets install criteria
 * (HTTPS + manifest + a registered service worker with a fetch handler). It can
 * fire before React mounts, so we capture it in index.html and sync here.
 */
let deferredPrompt = null;
let initialized = false;

const readWindowPrompt = () => {
  if (typeof window === 'undefined') return null;
  return window.__kayiDeferredInstallPrompt || null;
};

const syncDeferredPrompt = () => {
  const fromWindow = readWindowPrompt();
  if (fromWindow) {
    deferredPrompt = fromWindow;
  }
  return deferredPrompt;
};

export const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  try {
    return (
      window.matchMedia?.('(display-mode: standalone)')?.matches === true ||
      window.navigator?.standalone === true ||
      document.referrer.startsWith('android-app://')
    );
  } catch {
    return false;
  }
};

export const getDeferredPrompt = () => syncDeferredPrompt();

/** Register listeners and sync any prompt captured before React booted. */
export const initPwaInstall = () => {
  if (typeof window === 'undefined' || initialized) return;
  initialized = true;
  syncDeferredPrompt();

  if (!window.__kayiPwaEarlyCapture) {
    window.addEventListener('beforeinstallprompt', (event) => {
      event.preventDefault();
      deferredPrompt = event;
      window.__kayiDeferredInstallPrompt = event;
      window.dispatchEvent(new Event('kayi-install-available'));
    });

    window.addEventListener('appinstalled', () => {
      deferredPrompt = null;
      window.__kayiDeferredInstallPrompt = null;
      window.dispatchEvent(new Event('kayi-install-done'));
    });
  }
};

/**
 * Show the native install prompt.
 * @returns {Promise<'accepted'|'dismissed'|'unavailable'>}
 */
export const promptInstall = async () => {
  const prompt = syncDeferredPrompt();
  if (!prompt) return 'unavailable';
  deferredPrompt = null;
  window.__kayiDeferredInstallPrompt = null;
  try {
    prompt.prompt();
    const choice = await prompt.userChoice;
    if (choice?.outcome === 'accepted') {
      window.dispatchEvent(new Event('kayi-install-done'));
    }
    return choice?.outcome || 'dismissed';
  } catch {
    return 'unavailable';
  }
};

/** React hook exposing install availability + a trigger. */
export const usePwaInstall = () => {
  const [canInstall, setCanInstall] = useState(!!getDeferredPrompt());
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const onAvailable = () => setCanInstall(!!getDeferredPrompt());
    const onDone = () => {
      setCanInstall(false);
      setInstalled(true);
    };
    window.addEventListener('kayi-install-available', onAvailable);
    window.addEventListener('kayi-install-done', onDone);
    setCanInstall(!!getDeferredPrompt());
    setInstalled(isStandalone());
    return () => {
      window.removeEventListener('kayi-install-available', onAvailable);
      window.removeEventListener('kayi-install-done', onDone);
    };
  }, []);

  const install = useCallback(async () => promptInstall(), []);

  return { canInstall, installed, install };
};
